import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';

/**
 * DetailBlock
 *
 * Basic wrapper for all Detail Blocks on Detail Views.
 *
 * @prop {Element} children
 * @prop {String} className - Extends default className, does not overwrite.
 * @prop {Boolean} [transparent=false]
 * @prop {Boolean} [noPadding=false]
 */

export default function DetailBlock({ children, className, transparent, noPadding }) {
    return (
        <div className={classnames('detail-block', className, { transparent, padding: !noPadding })}>{children}</div>
    );
}

DetailBlock.defaultProps = {
    transparent: false,
    noPadding: false,
};

DetailBlock.propTypes = {
    children: PropTypes.node,
    transparent: PropTypes.bool,
    noPadding: PropTypes.bool,
    className: PropTypes.string,
};

/**
 * Header
 *
 * Header wrapper uses Flexbox for layout, so all children can be positioned nicely.
 *
 * @prop {Element} children
 * @prop {Boolean} [noRuler=false]

 */

export function Header({ children, noRuler }) {
    return <div className={classnames('detail-block-header', { ruler: !noRuler })}>{children}</div>;
}

Header.propTypes = {
    children: PropTypes.any,
    noRuler: PropTypes.bool,
};

Header.defaultProps = {
    noRuler: false,
};

/**
 * Title
 *
 * Title wrapper can take either a text prop or children.
 *
 * @prop {Element} [children]
 * @prop {String}  [title]
 */

export function Title({ text, children }) {
    const titleText = children || text;
    return <h4 className="detail-block-title">{titleText}</h4>;
}

Title.propTypes = {
    text: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    children: PropTypes.node,
};

/**
 * Subtitle
 *
 * Subtitle wrapper can take either a text prop or children. Applies a smaller & itatlic text style to header.
 *
 * @prop {Element} [children]
 * @prop {String}  [title]
 */

export function Subtitle({ text, children }) {
    const subtitleText = children || text;
    return <span className="detail-block-subtitle">{subtitleText}</span>;
}

Subtitle.propTypes = {
    text: PropTypes.string,
    children: PropTypes.node,
};

/**
 * EmptyText
 *
 * Empty text is a italic style wrapper for displaying when no other info to display
 *
 * @prop {Element} [children]
 * @prop {String}  [title]
 */

export function EmptyText({ text, children }) {
    const emptyText = children || text;
    return <span className="detail-block-empty-text">{emptyText}</span>;
}

EmptyText.propTypes = {
    text: PropTypes.string,
    children: PropTypes.node,
};

/**
 * EditButton
 *
 * EditButton to show when DetailBlock is editable.
 *
 * @prop {Function} onClick
 */

export function EditButton({ onClick }) {
    return (
        <button aria-label="edit" className="detail-block-edit" onClick={onClick}>
            <i aria-hidden="true" className="ni ni-pencil" />
        </button>
    );
}

EditButton.propTypes = {
    onClick: PropTypes.func.isRequired,
    className: PropTypes.string,
};

/**
 * EditControls
 *
 * EditControls contain a save and cancel button and take handlers for both as props.
 *
 * @prop {Function} onCancel
 * @prop {Function} onSave
 * @prop {Boolean}  isSaving
 */

export function EditControls({ onCancel, onSave, isSaving }) {
    return (
        <div className="edit-controls" key="edit-controls">
            <button
                className={classnames('btn button-transparent-secondary', { disabled: isSaving })}
                onClick={onCancel}
                disabled={isSaving}
            >
                Cancel
            </button>{' '}
            <button
                className={classnames('button-transparent', { disabled: isSaving })}
                onClick={onSave}
                disabled={isSaving}
            >
                Save
            </button>
        </div>
    );
}

EditControls.propTypes = {
    onCancel: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    isSaving: PropTypes.bool,
};

/**
 * createEditableDetailBlock
 *
 * Higher order component that makes a DetailBlock inline-editable by
 * passing editing controls & state props to a component using DetailBlock.
 *
 * @param {React.Component} DetailBlockComponent - The component expecting editing API as props.
 */

export function createEditableDetailBlock(DetailBlockComponent) {
    class EditableDetailBlock extends React.Component {
        constructor(props) {
            super(props);

            this.state = {
                isEditing: false,
                isSaving: false,
            };

            this.openEdit = this.openEdit.bind(this);
            this.cancelEdit = this.cancelEdit.bind(this);
            this.saveEdit = this.saveEdit.bind(this);
        }

        openEdit() {
            this.setState({
                isEditing: true,
            });
        }

        cancelEdit() {
            if (!this.state.isSaving) {
                this.setState({
                    isEditing: false,
                });
            }
        }

        saveEdit(handleSave) {
            return () => {
                if (!this.state.isSaving) {
                    this.setState({
                        isSaving: true,
                    });

                    return handleSave().then(() => {
                        this.setState({
                            isEditing: false,
                            isSaving: false,
                        });
                    });
                }
            };
        }

        render() {
            return (
                <DetailBlockComponent
                    {...this.props}
                    isEditing={this.state.isEditing}
                    isSaving={this.state.isSaving}
                    openEdit={this.openEdit}
                    cancelEdit={this.cancelEdit}
                    saveEdit={this.saveEdit}
                />
            );
        }
    }

    return EditableDetailBlock;
}
